import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Plus, Trash2, Building2, CalendarDays, Users, ClipboardCheck, Layers, ExternalLink, Check, X, Inbox, KeyRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Navbar } from "@/components/Navbar";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/lib/i18n";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";

const EMPTY = { name: "", slug: "", description: "", city: "", country: "", admin_name: "", admin_email: "", admin_password: "" };

export default function SuperAdmin() {
  const { user } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [assocs, setAssocs] = useState([]);
  const [requests, setRequests] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [adminUsers, setAdminUsers] = useState([]);
  const [pwdTarget, setPwdTarget] = useState(null);
  const [newPwd, setNewPwd] = useState("");
  const [savingPwd, setSavingPwd] = useState(false);
  const [userQuery, setUserQuery] = useState("");
  const [userResults, setUserResults] = useState(null);
  const [searching, setSearching] = useState(false);

  const load = useCallback(() => {
    api.get("/superadmin/stats").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/associations").then(({ data }) => setAssocs(data)).catch(() => {});
    api.get("/superadmin/association-requests").then(({ data }) => setRequests(data)).catch(() => {});
    api.get("/superadmin/admin-users").then(({ data }) => setAdminUsers(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (user === undefined) return;
    if (!user) return navigate("/login");
    if (user.role !== "super_admin") return navigate("/");
    load();
  }, [user, navigate, load]);

  if (!user || user.role !== "super_admin") return <div className="min-h-screen bg-background" />;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    try {
      await api.post("/superadmin/associations", form);
      toast.success(t("saved"));
      setOpen(false);
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const remove = async (slug) => {
    if (!window.confirm(t("confirm_delete"))) return;
    await api.delete(`/superadmin/associations/${slug}`);
    load();
  };

  const approveReq = async (id) => {
    try {
      await api.post(`/superadmin/association-requests/${id}/approve`);
      toast.success(t("request_approved"));
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const rejectReq = async (id) => {
    const reason = window.prompt(t("reject") + " — " + t("message_optional")) ?? "";
    try {
      await api.post(`/superadmin/association-requests/${id}/reject`, { reason });
      toast.success(t("request_rejected"));
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const pendingReqs = requests.filter((r) => r.status === "pending");

  const searchUsers = async () => {
    if (userQuery.trim().length < 3) return;
    setSearching(true);
    try {
      const { data } = await api.get(`/superadmin/users/search`, { params: { q: userQuery.trim() } });
      setUserResults(data);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSearching(false);
    }
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`${t("confirm_delete")} — ${u.email}`)) return;
    try {
      await api.delete(`/superadmin/users/${u.id}`);
      toast.success(t("user_deleted"));
      setUserResults((r) => r.filter((x) => x.id !== u.id));
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const roleLabel = (r) => ({
    association_admin: t("role_association_admin"), mosque_admin: t("role_mosque_admin"),
    moderator: t("role_moderator_label"), member: t("role_member_label"), super_admin: t("role_super_admin_label"),
  }[r] || r);

  const savePassword = async () => {
    setSavingPwd(true);
    try {
      await api.put(`/superadmin/users/${pwdTarget.id}/password`, { password: newPwd.trim() });
      toast.success(t("password_updated"));
      setPwdTarget(null);
      setNewPwd("");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSavingPwd(false);
    }
  };

  const cards = [
    { icon: Layers, label: t("associations"), value: stats?.associations },
    { icon: Building2, label: t("mosques"), value: stats?.mosques },
    { icon: CalendarDays, label: t("events"), value: stats?.events },
    { icon: Users, label: t("total_users"), value: stats?.users },
    { icon: ClipboardCheck, label: t("total_registrations"), value: stats?.registrations },
  ];

  return (
    <div className="min-h-screen bg-background blueprint-grid">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <div className="flex items-center justify-between mb-10">
          <div>
            <p className="text-xs font-mono-data uppercase tracking-[0.2em] text-accent mb-1">{t("super_admin")}</p>
            <h1 className="font-heading font-extrabold text-2xl sm:text-3xl tracking-tight">{t("dashboard")}</h1>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-association-btn" className="rounded-full active:scale-95">
                <Plus className="h-4 w-4 mr-2" /> {t("new_association")}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-heading">{t("new_association")}</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label>{t("assoc_name")}</Label><Input data-testid="assoc-form-name" value={form.name} onChange={set("name")} /></div>
                  <div className="space-y-1.5"><Label>{t("slug")}</Label><Input data-testid="assoc-form-slug" value={form.slug} onChange={set("slug")} placeholder="london" /></div>
                </div>
                <div className="space-y-1.5"><Label>{t("description")}</Label><Textarea rows={2} data-testid="assoc-form-description" value={form.description} onChange={set("description")} /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label>{t("city")}</Label><Input data-testid="assoc-form-city" value={form.city} onChange={set("city")} /></div>
                  <div className="space-y-1.5"><Label>{t("country")}</Label><Input data-testid="assoc-form-country" value={form.country} onChange={set("country")} /></div>
                </div>
                <div className="border-t border-border pt-4 space-y-4">
                  <div className="space-y-1.5"><Label>{t("admin_name")}</Label><Input data-testid="assoc-form-admin-name" value={form.admin_name} onChange={set("admin_name")} /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5"><Label>{t("admin_email")}</Label><Input type="email" data-testid="assoc-form-admin-email" value={form.admin_email} onChange={set("admin_email")} /></div>
                    <div className="space-y-1.5"><Label>{t("admin_password")}</Label><Input type="password" data-testid="assoc-form-admin-password" autoCapitalize="off" autoCorrect="off" spellCheck={false} value={form.admin_password} onChange={set("admin_password")} /></div>
                  </div>
                </div>
                <Button data-testid="assoc-form-submit" onClick={submit}
                  disabled={!form.name || !form.slug || !form.admin_name || !form.admin_email || form.admin_password.length < 6}
                  className="w-full rounded-full active:scale-95">
                  {t("create")}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="superadmin-stats">
          {cards.map((c) => (
            <div key={c.label} className="border border-border rounded-lg bg-card p-5">
              <c.icon className="h-4 w-4 text-accent" />
              <p className="font-mono-data font-bold text-3xl mt-3">{c.value ?? "—"}</p>
              <p className="text-xs text-muted-foreground mt-1">{c.label}</p>
            </div>
          ))}
        </div>

        <h2 className="font-heading font-bold text-lg mt-12 mb-5 flex items-center gap-2.5">
          <Inbox className="h-4 w-4 text-accent" /> {t("pending_requests")}
          {pendingReqs.length > 0 && <Badge className="text-[10px]" data-testid="pending-requests-badge">{pendingReqs.length}</Badge>}
        </h2>
        {requests.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center border border-dashed border-border rounded-lg" data-testid="no-requests">{t("no_results")}</p>
        ) : (
          <div className="border border-border rounded-lg overflow-hidden bg-card" data-testid="assoc-requests-table">
            {requests.map((r) => (
              <div key={r.id} className="flex flex-wrap items-center gap-4 px-5 py-4 border-b border-border last:border-0">
                <div className="min-w-0 flex-1">
                  <p className="font-heading font-semibold">{r.name} <span className="font-mono-data text-xs text-muted-foreground">/{r.slug}</span></p>
                  <p className="text-xs text-muted-foreground mt-0.5">{r.city}{r.country ? `, ${r.country}` : ""} · {r.contact_name} · {r.contact_email}</p>
                  {r.message && <p className="text-xs text-muted-foreground italic mt-1">"{r.message}"</p>}
                </div>
                <Badge variant={r.status === "pending" ? "default" : "secondary"} className="text-[10px]">{t(`req_${r.status}`)}</Badge>
                {r.status === "pending" && (
                  <div className="flex gap-2">
                    <Button size="sm" data-testid="approve-request-btn" onClick={() => approveReq(r.id)} className="rounded-full h-8 text-xs active:scale-95 bg-emerald-600 hover:bg-emerald-700 text-white">
                      <Check className="h-3 w-3 mr-1.5" /> {t("approve")}
                    </Button>
                    <Button size="sm" variant="outline" data-testid="reject-request-btn" onClick={() => rejectReq(r.id)} className="rounded-full h-8 text-xs active:scale-95 text-destructive border-destructive/40">
                      <X className="h-3 w-3 mr-1.5" /> {t("reject")}
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        <h2 className="font-heading font-bold text-lg mt-12 mb-5">{t("associations")}</h2>
        <div className="border border-border rounded-lg overflow-hidden bg-card" data-testid="superadmin-assoc-table">
          {assocs.map((a) => (
            <div key={a.id} className="flex items-center gap-4 px-5 py-4 border-b border-border last:border-0 hover:bg-muted/50 transition-colors">
              <div className="min-w-0 flex-1">
                <p className="font-heading font-semibold">{a.name}</p>
                <p className="text-xs font-mono-data text-muted-foreground">/{a.slug} · {a.city} · {a.mosques_count} {t("mosques").toLowerCase()} · {a.members_count} {t("members")}</p>
              </div>
              <Link to={`/${a.slug}`} data-testid="visit-assoc-btn">
                <Button variant="outline" size="sm" className="rounded-full h-8 text-xs active:scale-95">
                  <ExternalLink className="h-3 w-3 mr-1.5" /> {t("visit_page")}
                </Button>
              </Link>
              <Button variant="ghost" size="icon" data-testid="delete-assoc-btn" onClick={() => remove(a.slug)} className="h-8 w-8 text-destructive">
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          ))}
        </div>
        <h2 className="font-heading font-bold text-lg mt-12 mb-5 flex items-center gap-2.5">
          <KeyRound className="h-4 w-4 text-accent" /> {t("admin_accounts")}
        </h2>
        <div className="border border-border rounded-lg overflow-hidden bg-card" data-testid="admin-users-table">
          {adminUsers.length === 0 && <p className="text-sm text-muted-foreground py-6 text-center">{t("no_results")}</p>}
          {adminUsers.map((u) => (
            <div key={u.id} className="flex flex-wrap items-center gap-3 px-5 py-3.5 border-b border-border last:border-0 hover:bg-muted/50 transition-colors">
              <div className="min-w-0 flex-1">
                <p className="font-heading font-semibold text-sm">{u.name} <span className="font-mono-data text-xs text-muted-foreground">{u.email}</span></p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {u.association_name}{u.mosque_names.length > 0 ? ` · ${u.mosque_names.join(", ")}` : ""}
                </p>
              </div>
              <Badge variant="secondary" className="text-[10px]">
                {u.role === "association_admin" ? t("role_association_admin") : t("role_mosque_admin")}
              </Badge>
              <Button variant="outline" size="sm" data-testid={`change-pwd-${u.email}`}
                onClick={() => { setPwdTarget(u); setNewPwd(""); }}
                className="rounded-full h-8 text-xs active:scale-95">
                <KeyRound className="h-3 w-3 mr-1.5" /> {t("change_password_btn")}
              </Button>
            </div>
          ))}
        </div>

        <h2 className="font-heading font-bold text-lg mt-12 mb-5 flex items-center gap-2.5">
          <Users className="h-4 w-4 text-accent" /> {t("user_search_title")}
        </h2>
        <div className="flex gap-2 mb-4">
          <Input value={userQuery} onChange={(e) => setUserQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && searchUsers()}
            placeholder={t("user_search_placeholder")} data-testid="user-search-input" className="max-w-sm" />
          <Button variant="outline" onClick={searchUsers} disabled={searching || userQuery.trim().length < 3}
            data-testid="user-search-btn" className="rounded-full active:scale-95">
            {t("user_search_btn")}
          </Button>
        </div>
        {userResults && (
          <div className="border border-border rounded-lg overflow-hidden bg-card" data-testid="user-search-results">
            {userResults.length === 0 && <p className="text-sm text-muted-foreground py-6 text-center">{t("no_results")}</p>}
            {userResults.map((u) => (
              <div key={u.id} className="flex flex-wrap items-center gap-3 px-5 py-3.5 border-b border-border last:border-0 hover:bg-muted/50 transition-colors">
                <div className="min-w-0 flex-1">
                  <p className="font-heading font-semibold text-sm">{u.name} <span className="font-mono-data text-xs text-muted-foreground">{u.email}</span></p>
                  {u.association_name && <p className="text-xs text-muted-foreground mt-0.5">{u.association_name}</p>}
                </div>
                <Badge variant="secondary" className="text-[10px]">{roleLabel(u.role)}</Badge>
                {u.role !== "super_admin" && (
                  <Button variant="ghost" size="icon" data-testid={`delete-user-${u.email}`}
                    onClick={() => deleteUser(u)} className="h-8 w-8 text-destructive">
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}

        <Dialog open={!!pwdTarget} onOpenChange={(o) => !o && setPwdTarget(null)}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle className="font-heading">{t("change_password_btn")}</DialogTitle>
            </DialogHeader>
            {pwdTarget && (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">{pwdTarget.name} — <span className="font-mono-data text-xs">{pwdTarget.email}</span></p>
                <div className="space-y-1.5">
                  <Label>{t("new_password")}</Label>
                  <Input type="text" data-testid="new-password-input" value={newPwd}
                    autoCapitalize="off" autoCorrect="off" spellCheck={false} autoComplete="off"
                    onChange={(e) => setNewPwd(e.target.value)} placeholder={t("pwd_min_hint")} />
                </div>
                <Button data-testid="save-password-btn" onClick={savePassword}
                  disabled={savingPwd || newPwd.trim().length < 8}
                  className="w-full rounded-full active:scale-95">
                  {t("save")}
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
